/**
 * The fly-in (spec 02 §9.1, research R§7 "Level flow" 2): after the scroll the players, in bubbles,
 * fly in straight lines from where they were to their starts at 1 px per frame; the drop-in begins
 * once both are there. Integrator-owned, with the flow.
 */

import type { World } from './types';

/** Frames a player's flight takes: 1 px per frame along the longer axis. */
function flightFrames(world: World, id: 0 | 1): number {
  const f = world.flow;
  const from = f.flyFrom[id];
  const to = f.flyTo[id];
  return Math.max(Math.abs(to.x - from.x), Math.abs(to.y - from.y));
}

/** Frames the whole fly-in takes: the longer of the players' flights (a player out of the game waits at its start). */
export function flyInFrames(world: World): number {
  return Math.max(...([0, 1] as const).map((id) => (world.players[id].state === 'play' ? flightFrames(world, id) : 0)));
}

/**
 * Put the players `frame` frames into their flights. Positions stay whole pixels, as sprite registers
 * are: each coordinate moves by its share of the flight, rounded.
 */
export function flyInFrame(world: World, frame: number): void {
  const f = world.flow;
  f.flyFrame = frame;
  for (const id of [0, 1] as const) {
    const p = world.players[id];
    if (p.state !== 'play') continue;
    const n = flightFrames(world, id);
    const t = n === 0 ? 1 : Math.min(1, frame / n);
    const from = f.flyFrom[id];
    const to = f.flyTo[id];
    p.x = Math.round(from.x + (to.x - from.x) * t);
    p.y = Math.round(from.y + (to.y - from.y) * t);
  }
}
